import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { ExamService } from '../services/exam.service';
import { ExamSubjectService } from '../services/examsubject.service';
import { ExamChapterService } from '../services/examchapter.service';
import { GradeService } from '../services/grade.service';
import { SubjectService } from '../services/subject.service';
import { ChapterService } from '../services/chapter.service';
import {
  Exam,
  ExamSubject,
  ExamChapter,
  Grade,
  Subject,
  Chapter,
  BulkCreateExamChaptersRequest,
  CopyExamRequest
} from '../types';

const ExamManagement: React.FC = () => {
  const { user, logout } = useAuth();
  const [exams, setExams] = useState<Exam[]>([]);
  const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
  const [examSubjects, setExamSubjects] = useState<ExamSubject[]>([]);
  const [selectedExamSubjectId, setSelectedExamSubjectId] = useState<number | null>(null);
  const [examChapters, setExamChapters] = useState<ExamChapter[]>([]);

  // Modal states
  const [showAddChapterModal, setShowAddChapterModal] = useState(false);
  const [showCreateExamModal, setShowCreateExamModal] = useState(false);
  const [showRenameExamModal, setShowRenameExamModal] = useState(false);
  const [showCopyExamModal, setShowCopyExamModal] = useState(false);
  const [renameExamData, setRenameExamData] = useState({ name: '', code: '', description: '', instructions: '' });
  const [showRenameExamSubjectModal, setShowRenameExamSubjectModal] = useState(false);
  const [renameExamSubjectData, setRenameExamSubjectData] = useState({ name: '', description: '', weightage: '', display_order: '' });
  const [showRenameExamChapterModal, setShowRenameExamChapterModal] = useState(false);
  const [renameExamChapterData, setRenameExamChapterData] = useState({ name: '' });
  const [selectedExamChapterId, setSelectedExamChapterId] = useState<number | null>(null);
  const [examFormData, setExamFormData] = useState({
    name: '',
    code: '',
    description: '',
    instructions: ''
  });
  const [copyExamFormData, setCopyExamFormData] = useState({
    new_name: '',
    new_code: '',
    copy_subjects: true,
    copy_chapters: true
  });
  const [showCreateExamSubjectModal, setShowCreateExamSubjectModal] = useState(false);
  const [examSubjectFormData, setExamSubjectFormData] = useState({
    name: '',
    description: '',
    weightage: '',
    display_order: ''
  });
  const [grades, setGrades] = useState<Grade[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedGradeId, setSelectedGradeId] = useState<number | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [selectedChapterIds, setSelectedChapterIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load exams on component mount
  useEffect(() => {
    loadExams();
    loadGrades();
  }, []);

  // Load exam subjects when exam is selected
  useEffect(() => {
    if (selectedExamId) {
      loadExamSubjects(selectedExamId);
    } else {
      setExamSubjects([]);
      setSelectedExamSubjectId(null);
    }
  }, [selectedExamId]);

  // Load exam chapters when exam subject is selected
  useEffect(() => {
    if (selectedExamSubjectId) {
      loadExamChapters(selectedExamSubjectId);
    } else {
      setExamChapters([]);
    }
  }, [selectedExamSubjectId]);

  // Load subjects when grade is selected in modal
  useEffect(() => {
    if (selectedGradeId) {
      loadSubjects(selectedGradeId);
    } else {
      setSubjects([]);
      setSelectedSubjectId(null);
    }
  }, [selectedGradeId]);

  // Load chapters when subject is selected in modal
  useEffect(() => {
    if (selectedSubjectId) {
      loadChapters(selectedSubjectId);
    } else {
      setChapters([]);
      setSelectedChapterIds([]);
      setSelectAll(false);
    }
  }, [selectedSubjectId]);

  const loadExams = async () => {
    try {
      setLoading(true);
      const response = await ExamService.getExams();
      setExams(response.exams || []);
    } catch (err) {
      setError('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const loadExamSubjects = async (examId: number) => {
    try {
      const response = await ExamSubjectService.getExamSubjectsByExamId(examId);
      setExamSubjects(response.examSubjects || []);
    } catch (err) {
      setError('Failed to load exam subjects');
    }
  };

  const loadExamChapters = async (examSubjectId: number) => {
    try {
      const response = await ExamChapterService.getExamChaptersByExamSubjectId(examSubjectId);
      setExamChapters(response.examChapters || []);
    } catch (err) {
      setError('Failed to load exam chapters');
    }
  };

  const loadGrades = async () => {
    try {
      const response = await GradeService.getAllGrades();
      setGrades(response || []);
    } catch (err) {
      setError('Failed to load grades');
    }
  };

  const loadSubjects = async (gradeId: number) => {
    try {
      const response = await SubjectService.getSubjectsByGradeId(gradeId);
      setSubjects(response || []);
    } catch (err) {
      setError('Failed to load subjects');
    }
  };

  const loadChapters = async (subjectId: number) => {
    try {
      const response = await ChapterService.getChaptersBySubjectId(subjectId);
      setChapters(response || []);
    } catch (err) {
      setError('Failed to load chapters');
    }
  };

  const handleExamSelect = (examId: number) => {
    setSelectedExamId(examId);
    setSelectedExamSubjectId(null);
  };

  const handleExamSubjectSelect = (examSubjectId: number) => {
    setSelectedExamSubjectId(examSubjectId);
  };

  const handleOpenAddChapterModal = () => {
    setShowAddChapterModal(true);
    setSelectedGradeId(null);
    setSelectedSubjectId(null);
    setSelectedChapterIds([]);
    setSelectAll(false);
  };

  const handleCloseAddChapterModal = () => {
    setShowAddChapterModal(false);
  };

  const handleChapterCheckboxChange = (chapterId: number, checked: boolean) => {
    if (checked) {
      setSelectedChapterIds(prev => [...prev, chapterId]);
    } else {
      setSelectedChapterIds(prev => prev.filter(id => id !== chapterId));
      setSelectAll(false);
    }
  };

  const handleSelectAllChange = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedChapterIds(chapters.map(chapter => chapter.id));
    } else {
      setSelectedChapterIds([]);
    }
  };

  const handleSaveChapters = async () => {
    if (!selectedExamSubjectId || selectedChapterIds.length === 0) {
      setError('Please select chapters to add');
      return;
    }

    try {
      setLoading(true);
      const bulkData: BulkCreateExamChaptersRequest = {
        exam_subject_id: selectedExamSubjectId,
        chapters: selectedChapterIds.map(chapterId => {
          const chapter = chapters.find(c => c.id === chapterId);
          return {
            chapter_id: chapterId,
            name: chapter?.name
          };
        })
      };

      await ExamChapterService.bulkCreateExamChapters(bulkData);
      await loadExamChapters(selectedExamSubjectId);
      handleCloseAddChapterModal();
    } catch (err) {
      setError('Failed to add chapters');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExamChapter = async (examChapterId: number) => {
    if (!window.confirm('Are you sure you want to delete this chapter?')) {
      return;
    }

    try {
      await ExamChapterService.deleteExamChapter(examChapterId);
      if (selectedExamSubjectId) {
        await loadExamChapters(selectedExamSubjectId);
      }
    } catch (err) {
      setError('Failed to delete chapter');
    }
  };

  const handleOpenCreateExamModal = () => {
    setShowCreateExamModal(true);
    setExamFormData({
      name: '',
      code: '',
      description: '',
      instructions: ''
    });
  };

  const handleCloseCreateExamModal = () => {
    setShowCreateExamModal(false);
  };

  const handleExamFormChange = (field: string, value: string) => {
    setExamFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateExam = async () => {
    if (!examFormData.name.trim() || !examFormData.code.trim()) {
      setError('Name and code are required');
      return;
    }

    try {
      setLoading(true);
      await ExamService.createExam({
        name: examFormData.name.trim(),
        code: examFormData.code.trim(),
        description: examFormData.description.trim() || undefined,
        instructions: examFormData.instructions.trim() || undefined
      });
      await loadExams();
      handleCloseCreateExamModal();
    } catch (err) {
      setError('Failed to create exam');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateExamSubjectModal = () => {
    setShowCreateExamSubjectModal(true);
    setExamSubjectFormData({
      name: '',
      description: '',
      weightage: '',
      display_order: ''
    });
  };

  const handleCloseCreateExamSubjectModal = () => {
    setShowCreateExamSubjectModal(false);
  };

  const handleExamSubjectFormChange = (field: string, value: string) => {
    setExamSubjectFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateExamSubject = async () => {
    if (!examSubjectFormData.name.trim() || !selectedExamId) {
      setError('Name is required and an exam must be selected');
      return;
    }

    try {
      setLoading(true);
      const subjectData: any = {
        exam_id: selectedExamId,
        name: examSubjectFormData.name.trim()
      };

      if (examSubjectFormData.description.trim()) {
        subjectData.description = examSubjectFormData.description.trim();
      }
      if (examSubjectFormData.weightage.trim()) {
        subjectData.weightage = parseFloat(examSubjectFormData.weightage);
      }
      if (examSubjectFormData.display_order.trim()) {
        subjectData.display_order = parseInt(examSubjectFormData.display_order);
      }

      await ExamSubjectService.createExamSubject(subjectData);
      if (selectedExamId) {
        await loadExamSubjects(selectedExamId);
      }
      handleCloseCreateExamSubjectModal();
    } catch (err) {
      setError('Failed to create exam subject');
    } finally {
      setLoading(false);
    }
  };

  // Rename Exam Functions
  const handleOpenRenameExamModal = () => {
    const selectedExam = exams.find(exam => exam.id === selectedExamId);
    if (selectedExam) {
      setRenameExamData({
        name: selectedExam.name,
        code: selectedExam.code,
        description: selectedExam.description || '',
        instructions: selectedExam.instructions || ''
      });
      setShowRenameExamModal(true);
    }
  };

  const handleCloseRenameExamModal = () => {
    setShowRenameExamModal(false);
  };

  const handleRenameExamFormChange = (field: string, value: string) => {
    setRenameExamData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRenameExam = async () => {
    if (!renameExamData.name.trim() || !renameExamData.code.trim() || !selectedExamId) {
      setError('Name and code are required');
      return;
    }

    try {
      setLoading(true);
      await ExamService.updateExam(selectedExamId, {
        name: renameExamData.name.trim(),
        code: renameExamData.code.trim(),
        description: renameExamData.description.trim() || undefined,
        instructions: renameExamData.instructions.trim() || undefined
      });
      await loadExams();
      handleCloseRenameExamModal();
    } catch (err) {
      setError('Failed to rename exam');
    } finally {
      setLoading(false);
    }
  };

  // Copy Exam Functions
  const handleOpenCopyExamModal = () => {
    const selectedExam = exams.find(exam => exam.id === selectedExamId);
    if (selectedExam) {
      setCopyExamFormData({
        new_name: selectedExam.name + ' - Copy',
        new_code: '',
        copy_subjects: true,
        copy_chapters: true
      });
      setShowCopyExamModal(true);
    }
  };

  const handleCloseCopyExamModal = () => {
    setShowCopyExamModal(false);
  };

  const handleCopyExamFormChange = (field: string, value: string | boolean) => {
    setCopyExamFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCopyExam = async () => {
    if (!copyExamFormData.new_name.trim() || !copyExamFormData.new_code.trim() || !selectedExamId) {
      setError('Name and code are required');
      return;
    }

    if (copyExamFormData.new_code.length > 12) {
      setError('Code must be 12 characters or less');
      return;
    }

    try {
      setLoading(true);
      const copyData: CopyExamRequest = {
        new_name: copyExamFormData.new_name.trim(),
        new_code: copyExamFormData.new_code.trim(),
        copy_subjects: copyExamFormData.copy_subjects,
        copy_chapters: copyExamFormData.copy_chapters
      };

      await ExamService.copyExam(selectedExamId, copyData);
      await loadExams();
      handleCloseCopyExamModal();
    } catch (err) {
      setError('Failed to copy exam');
    } finally {
      setLoading(false);
    }
  };

  // Rename Exam Subject Functions
  const handleOpenRenameExamSubjectModal = () => {
    const selectedExamSubject = examSubjects.find(subject => subject.id === selectedExamSubjectId);
    if (selectedExamSubject) {
      setRenameExamSubjectData({
        name: selectedExamSubject.name,
        description: selectedExamSubject.description || '',
        weightage: selectedExamSubject.weightage?.toString() || '',
        display_order: selectedExamSubject.display_order?.toString() || ''
      });
      setShowRenameExamSubjectModal(true);
    }
  };

  const handleCloseRenameExamSubjectModal = () => {
    setShowRenameExamSubjectModal(false);
  };

  const handleRenameExamSubjectFormChange = (field: string, value: string) => {
    setRenameExamSubjectData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRenameExamSubject = async () => {
    if (!renameExamSubjectData.name.trim() || !selectedExamSubjectId) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      const subjectData: any = {
        name: renameExamSubjectData.name.trim()
      };

      if (renameExamSubjectData.description.trim()) {
        subjectData.description = renameExamSubjectData.description.trim();
      }
      if (renameExamSubjectData.weightage.trim()) {
        subjectData.weightage = parseFloat(renameExamSubjectData.weightage);
      }
      if (renameExamSubjectData.display_order.trim()) {
        subjectData.display_order = parseInt(renameExamSubjectData.display_order);
      }

      await ExamSubjectService.updateExamSubject(selectedExamSubjectId, subjectData);
      if (selectedExamId) {
        await loadExamSubjects(selectedExamId);
      }
      handleCloseRenameExamSubjectModal();
    } catch (err) {
      setError('Failed to rename exam subject');
    } finally {
      setLoading(false);
    }
  };

  // Rename Exam Chapter Functions
  const handleOpenRenameExamChapterModal = (examChapterId: number) => {
    const selectedExamChapter = examChapters.find(chapter => chapter.id === examChapterId);
    if (selectedExamChapter) {
      setSelectedExamChapterId(examChapterId);
      setRenameExamChapterData({
        name: selectedExamChapter.name
      });
      setShowRenameExamChapterModal(true);
    }
  };

  const handleCloseRenameExamChapterModal = () => {
    setShowRenameExamChapterModal(false);
    setSelectedExamChapterId(null);
  };

  const handleRenameExamChapterFormChange = (field: string, value: string) => {
    setRenameExamChapterData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRenameExamChapter = async () => {
    if (!renameExamChapterData.name.trim() || !selectedExamChapterId) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      await ExamChapterService.updateExamChapter(selectedExamChapterId, {
        name: renameExamChapterData.name.trim()
      });
      if (selectedExamSubjectId) {
        await loadExamChapters(selectedExamSubjectId);
      }
      handleCloseRenameExamChapterModal();
    } catch (err) {
      setError('Failed to rename exam chapter');
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = () => {
    if (!user) return 'User';
    
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else {
      return user.username || 'User';
    }
  };

  if (loading && exams.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="container">
      <header>
        <h1>Exam Management</h1>
        <div className="user-info">
          <span>Welcome, {getDisplayName()}</span>
          <Link to="/dashboard"><button className="notes-btn">Notes</button></Link>
          <Link to="/report"><button className="report-btn">Report</button></Link>
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>
      </header>

      <main>
        {error && <div className="error-message">{error}</div>}

        {/* Exams Dropdown */}
        <div className="panel">
          <h2>Exams</h2>
          <select 
            value={selectedExamId || ''} 
            onChange={(e) => handleExamSelect(Number(e.target.value))}
          >
            <option value="">Select an exam...</option>
            {exams.map(exam => (
              <option key={exam.id} value={exam.id}>
                {exam.name} ({exam.code})
              </option>
            ))}
          </select>
          <div className="actions">
            <button onClick={handleOpenCreateExamModal}>Create</button>
            <button disabled={!selectedExamId} onClick={handleOpenRenameExamModal}>Rename</button>
            <button disabled={!selectedExamId} onClick={handleOpenCopyExamModal}>Copy</button>
            <button disabled={!selectedExamId}>Delete</button>
          </div>
        </div>

        {/* Exam Subjects Dropdown */}
        {selectedExamId && (
          <div className="panel">
            <h2>Exam Subjects</h2>
            <select 
              value={selectedExamSubjectId || ''} 
              onChange={(e) => handleExamSubjectSelect(Number(e.target.value))}
            >
              <option value="">Select an exam subject...</option>
              {examSubjects.map(subject => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
            <div className="actions">
              <button onClick={handleOpenCreateExamSubjectModal}>Create</button>
              <button disabled={!selectedExamSubjectId} onClick={handleOpenRenameExamSubjectModal}>Rename</button>
              <button disabled={!selectedExamSubjectId}>Delete</button>
            </div>
          </div>
        )}

        {/* Exam Chapters */}
        {selectedExamSubjectId && (
          <div className="panel">
            <h2>Exam Chapters</h2>
            <div className="chapters-list">
              {examChapters.map(examChapter => (
                <div key={examChapter.id} className="chapter-item">
                  <span>{examChapter.name}</span>
                  {examChapter.chapter && (
                    <span className="chapter-info">
                      ({examChapter.chapter.subject.grade.name} - {examChapter.chapter.subject.name})
                    </span>
                  )}
                  <div className="chapter-actions">
                    <button onClick={() => handleOpenRenameExamChapterModal(examChapter.id)}>Rename</button>
                    <button onClick={() => handleDeleteExamChapter(examChapter.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={handleOpenAddChapterModal} className="add-chapter-btn">
              Add Chapter
            </button>
          </div>
        )}
      </main>

      {/* Create Exam Modal */}
      {showCreateExamModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Exam</h3>
              <button onClick={handleCloseCreateExamModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *:</label>
                <input
                  type="text"
                  value={examFormData.name}
                  onChange={(e) => handleExamFormChange('name', e.target.value)}
                  placeholder="Enter exam name"
                />
              </div>
              <div className="form-group">
                <label>Code *:</label>
                <input
                  type="text"
                  value={examFormData.code}
                  onChange={(e) => handleExamFormChange('code', e.target.value)}
                  placeholder="Enter exam code"
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={examFormData.description}
                  onChange={(e) => handleExamFormChange('description', e.target.value)}
                  placeholder="Enter exam description (optional)"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Instructions:</label>
                <textarea
                  value={examFormData.instructions}
                  onChange={(e) => handleExamFormChange('instructions', e.target.value)}
                  placeholder="Enter exam instructions (optional)"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseCreateExamModal}>Cancel</button>
              <button 
                onClick={handleCreateExam} 
                disabled={!examFormData.name.trim() || !examFormData.code.trim() || loading}
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Exam Subject Modal */}
      {showCreateExamSubjectModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Exam Subject</h3>
              <button onClick={handleCloseCreateExamSubjectModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *:</label>
                <input
                  type="text"
                  value={examSubjectFormData.name}
                  onChange={(e) => handleExamSubjectFormChange('name', e.target.value)}
                  placeholder="Enter exam subject name"
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={examSubjectFormData.description}
                  onChange={(e) => handleExamSubjectFormChange('description', e.target.value)}
                  placeholder="Enter exam subject description (optional)"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Weightage:</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={examSubjectFormData.weightage}
                  onChange={(e) => handleExamSubjectFormChange('weightage', e.target.value)}
                  placeholder="Enter weightage (optional)"
                />
              </div>
              <div className="form-group">
                <label>Display Order:</label>
                <input
                  type="number"
                  min="1"
                  value={examSubjectFormData.display_order}
                  onChange={(e) => handleExamSubjectFormChange('display_order', e.target.value)}
                  placeholder="Enter display order (optional)"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseCreateExamSubjectModal}>Cancel</button>
              <button 
                onClick={handleCreateExamSubject} 
                disabled={!examSubjectFormData.name.trim() || !selectedExamId || loading}
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Exam Modal */}
      {showRenameExamModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Rename Exam</h3>
              <button onClick={handleCloseRenameExamModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *:</label>
                <input
                  type="text"
                  value={renameExamData.name}
                  onChange={(e) => handleRenameExamFormChange('name', e.target.value)}
                  placeholder="Enter exam name"
                />
              </div>
              <div className="form-group">
                <label>Code *:</label>
                <input
                  type="text"
                  value={renameExamData.code}
                  onChange={(e) => handleRenameExamFormChange('code', e.target.value)}
                  placeholder="Enter exam code"
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={renameExamData.description}
                  onChange={(e) => handleRenameExamFormChange('description', e.target.value)}
                  placeholder="Enter exam description (optional)"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Instructions:</label>
                <textarea
                  value={renameExamData.instructions}
                  onChange={(e) => handleRenameExamFormChange('instructions', e.target.value)}
                  placeholder="Enter exam instructions (optional)"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseRenameExamModal}>Cancel</button>
              <button 
                onClick={handleRenameExam} 
                disabled={!renameExamData.name.trim() || !renameExamData.code.trim() || loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Exam Subject Modal */}
      {showRenameExamSubjectModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Rename Exam Subject</h3>
              <button onClick={handleCloseRenameExamSubjectModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *:</label>
                <input
                  type="text"
                  value={renameExamSubjectData.name}
                  onChange={(e) => handleRenameExamSubjectFormChange('name', e.target.value)}
                  placeholder="Enter exam subject name"
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={renameExamSubjectData.description}
                  onChange={(e) => handleRenameExamSubjectFormChange('description', e.target.value)}
                  placeholder="Enter exam subject description (optional)"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Weightage:</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={renameExamSubjectData.weightage}
                  onChange={(e) => handleRenameExamSubjectFormChange('weightage', e.target.value)}
                  placeholder="Enter weightage (optional)"
                />
              </div>
              <div className="form-group">
                <label>Display Order:</label>
                <input
                  type="number"
                  min="1"
                  value={renameExamSubjectData.display_order}
                  onChange={(e) => handleRenameExamSubjectFormChange('display_order', e.target.value)}
                  placeholder="Enter display order (optional)"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseRenameExamSubjectModal}>Cancel</button>
              <button 
                onClick={handleRenameExamSubject} 
                disabled={!renameExamSubjectData.name.trim() || loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Exam Chapter Modal */}
      {showRenameExamChapterModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Rename Exam Chapter</h3>
              <button onClick={handleCloseRenameExamChapterModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *:</label>
                <input
                  type="text"
                  value={renameExamChapterData.name}
                  onChange={(e) => handleRenameExamChapterFormChange('name', e.target.value)}
                  placeholder="Enter exam chapter name"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseRenameExamChapterModal}>Cancel</button>
              <button 
                onClick={handleRenameExamChapter} 
                disabled={!renameExamChapterData.name.trim() || loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Chapter Modal */}
      {showAddChapterModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Chapters</h3>
              <button onClick={handleCloseAddChapterModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-content">
              {/* Grade Dropdown */}
              <div className="form-group">
                <label>Grade:</label>
                <select 
                  value={selectedGradeId || ''} 
                  onChange={(e) => setSelectedGradeId(Number(e.target.value))}
                >
                  <option value="">Select a grade...</option>
                  {grades.map(grade => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject Dropdown */}
              {selectedGradeId && (
                <div className="form-group">
                  <label>Subject:</label>
                  <select 
                    value={selectedSubjectId || ''} 
                    onChange={(e) => setSelectedSubjectId(Number(e.target.value))}
                  >
                    <option value="">Select a subject...</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Chapters Checkboxes */}
              {selectedSubjectId && (
                <div className="form-group">
                  <label>Chapters:</label>
                  <div className="chapters-checkboxes">
                    <div className="select-all">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAllChange(e.target.checked)}
                        />
                        Select All
                      </label>
                    </div>
                    {chapters.map(chapter => (
                      <div key={chapter.id} className="chapter-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedChapterIds.includes(chapter.id)}
                            onChange={(e) => handleChapterCheckboxChange(chapter.id, e.target.checked)}
                          />
                          {chapter.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseAddChapterModal}>Cancel</button>
              <button 
                onClick={handleSaveChapters} 
                disabled={selectedChapterIds.length === 0 || loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Exam Modal */}
      {showCopyExamModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Copy Exam</h3>
              <button onClick={handleCloseCopyExamModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>New Name *:</label>
                <input
                  type="text"
                  value={copyExamFormData.new_name}
                  onChange={(e) => handleCopyExamFormChange('new_name', e.target.value)}
                  placeholder="Enter new exam name"
                />
              </div>
              <div className="form-group">
                <label>New Code * (max 12 characters):</label>
                <input
                  type="text"
                  value={copyExamFormData.new_code}
                  onChange={(e) => handleCopyExamFormChange('new_code', e.target.value)}
                  placeholder="Enter new exam code"
                  maxLength={12}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={copyExamFormData.copy_subjects}
                    onChange={(e) => handleCopyExamFormChange('copy_subjects', e.target.checked)}
                  />
                  Copy Subjects
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={copyExamFormData.copy_chapters}
                    onChange={(e) => handleCopyExamFormChange('copy_chapters', e.target.checked)}
                  />
                  Copy Chapters
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseCopyExamModal}>Cancel</button>
              <button
                onClick={handleCopyExam}
                disabled={!copyExamFormData.new_name.trim() || !copyExamFormData.new_code.trim() || loading}
              >
                {loading ? 'Copying...' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer>
        <p>&copy; 2025 Educational Content Management System</p>
      </footer>
    </div>
  );
};

export default ExamManagement;